import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { taskKeys } from '@/lib/queryKeys';
import { taskEndpoints } from '@/lib/api/endpoints';
import { apiPatch, operationsClient } from '@/lib/api';
import type { Task, UpdateTaskDto, TaskListResponse } from '@/types/task';

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useApiMutation<Task, UpdateTaskDto>({
    mutationFn: ({ id, ...dto }) =>
      apiPatch<Task, Omit<UpdateTaskDto, 'id'>>(operationsClient, taskEndpoints.update(id), dto),
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.all() });

      // Snapshot and optimistically update all list caches
      const listSnapshots = new Map<string, TaskListResponse>();
      queryClient
        .getQueriesData<TaskListResponse>({ queryKey: taskKeys.lists() })
        .forEach(([key, data]) => {
          if (data) {
            listSnapshots.set(JSON.stringify(key), data);
            queryClient.setQueryData<TaskListResponse>(key as string[], {
              ...data,
              tasks: data.tasks.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)),
            });
          }
        });

      // Snapshot and optimistically update detail cache
      const previousDetail = queryClient.getQueryData<Task>(taskKeys.detail(updated.id));
      if (previousDetail) {
        queryClient.setQueryData<Task>(taskKeys.detail(updated.id), {
          ...previousDetail,
          ...updated,
        });
      }

      return { listSnapshots, previousDetail };
    },
    onError: (_err, updated, context) => {
      const ctx = context as
        | {
            listSnapshots?: Map<string, TaskListResponse>;
            previousDetail?: Task;
          }
        | undefined;

      ctx?.listSnapshots?.forEach((data, keyStr) => {
        queryClient.setQueryData(JSON.parse(keyStr) as string[], data);
      });
      if (ctx?.previousDetail) {
        queryClient.setQueryData(taskKeys.detail(updated.id), ctx.previousDetail);
      }
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: taskKeys.countsAll() });
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(data.id) });
      toast.success('Task updated');
    },
  });
}
